import type { Messages } from './de';

/** Französisch. */
export const fr: Messages = {
  label: 'Français',
  tag: 'fr',

  format: {
    decimal: ',',
  },

  start: {
    resumeKicker: 'Évaluation en cours',
    resumeTitle: 'Continuer ?',
    resumeBody: (candidat: string, station: string) =>
      `Une évaluation inachevée existe pour ${candidat} à ${station}.`,
    resumeKeep: 'Continuer',
    resumeFresh: 'Recommencer',
    resumeNote: 'Recommencer ne supprime rien — l\'évaluation reste enregistrée.',
    pickKicker: 'Candidat',
    pickTitle: 'Qui est évalué ?',
    scan: 'Scanner le code QR',
    fromList: 'Choisir dans la liste',
    startStation: 'Démarrer la station',
  },

  backlog: {
    title: 'Pas encore transmis',
    packets: 'Paquets',
    files: 'Fichiers',
    rejected: 'abandonnés',
    kinds: {
      pakete: 'Évaluations (entrées du journal)',
      screenshot: 'Captures de la feuille',
      photo: 'Photos',
      video: 'Vidéos',
      audio: 'Enregistrements audio',
      handwriting: 'Notes manuscrites',
      annotation: 'Annotations',
      sonstige: 'Autres fichiers',
    },
    rejectedRow: 'Dont abandonnés',
    autoOff: 'La transmission automatique est désactivée. L’envoi se fait uniquement via « Envoyer maintenant ».',
    label: (pakete: number, dateien: number) =>
      `Pas encore transmis : ${pakete} paquets, ${dateien} fichiers`,
    state: {
      leer: 'Tout est transmis.',
      wartet: 'En attente du prochain essai. L’app réessaie d’elle-même.',
      sendet: 'Transmission en cours …',
      steckt: 'Certains éléments ne sont pas encore passés et attendent ici. Les évaluations sont enregistrées.',
    },
  },
  header: {
    stationPrefix: 'Station',
    candidate: 'Candidat',
    /** Steht im Kopf, solange der Ausweis noch nicht gescannt ist. */
    noCandidate: 'aucun candidat',
    remaining: 'Temps restant',
    total: 'Total',
    settings: 'Réglages',
    chooseExam: "Choisir l'examen et la station",
    startClock: 'Reprendre le chronomètre',
    pauseClock: 'Mettre en pause',
    resetClock: 'Réinitialiser le temps',
    setClock: 'Régler le temps de station',
    problemExam: 'Examen modifié ou supprimé sur le serveur',
  },

  bogen: {
    vignette: 'Vignette clinique',
    open: 'Ouvrir',
    task: 'Tâche',
    comment: 'Commentaire',
    comments: (n) => (n === 1 ? '1 commentaire' : `${n} commentaires`),
    fullScore: 'points complets',
    noScore: 'aucun point',
    reduced: 'réduit',
    adjusted: 'ajusté',
    pending: 'à définir',
    possible: (points) => `${points} pts possibles`,
    ofPoints: (value, max) => `${value} / ${max} pts`,
    points: (value) => `${value} pts`,
    progress: (done, total) => `${done} tâches évaluées sur ${total}`,
    commentsOn: (tasks) => `Commentaires sur la tâche ${tasks}`,
    submit: 'Envoyer l’évaluation',
    stationComment: 'Commentaire de station',
    stationCommentOn: (n: number) => `Commentaire de station · ${n}`,
    submitArm: 'Toucher à nouveau pour rendre',
    submitted: 'Envoyée',
    emptyTitle: 'Aucune grille chargée',
    emptyNote:
      'Il n’y a pas de grille d’évaluation pour cette station. Choisissez une autre station ou rechargez l’examen.',
    chooseStation: 'Choisir une station',
  },

  score: {
    upTo: (max) => `jusqu’à ${max} points`,
    undoOverride: 'Annuler la saisie manuelle — recalculer',
    criterionOf: (index, total) => `Critère ${index} sur ${total}`,
    clearChoice: 'Annuler le choix',
  },

  aufsicht: {
    kicker: 'Surveillance',
    titel: 'Message',
    gelesen: 'Lu',
    zugabe: (min: number, sek: number) =>
      `Surveillance : +${[min > 0 ? `${min} min` : '', sek > 0 ? `${sek} s` : ''].filter(Boolean).join(' ')} de temps de station`,
  },
  timer: {
    title: 'Temps de station',
    running: 'en cours',
    paused: 'en pause',
    expired: 'écoulé',
    resume: 'Reprendre',
    pause: 'Pause',
    reset: 'Réinitialiser',
    minutes: (n) => `${n} min`,
    fromStation: 'Station',
    hide: 'Masquer le temps restant',
  },


  unbekannteEinstellungen: (namen: string) => `Paramètres inconnus ignorés : ${namen}`,
  aufnahmeSymbol: 'Capture d’écran prise pour la direction de l’examen',
  preview: {
    laedt: 'Chargement de l’aperçu …',
    notFound: 'Cet aperçu n’existe plus — déjà récupéré ou expiré. Le recréer dans Relay.',
    network: 'Pas de connexion au service d’aperçu. Vérifier le réseau et scanner à nouveau.',
    clock: 'L’horloge de cet appareil est fausse. Régler l’heure et scanner à nouveau.',
    signature: 'Le service d’aperçu a refusé cet appareil.',
    decrypt: 'L’aperçu n’a pas pu être déchiffré.',
    version: 'Cet aperçu provient d’une version plus récente. Veuillez mettre à jour l’application.',
    tooLarge: 'L’aperçu est trop volumineux pour cet appareil.',
    format: 'L’aperçu est incomplet ou endommagé.',
    realExam: 'Cet examen est déjà sur l’appareil comme examen réel. L’aperçu ne l’écrase pas.',
    unbekannt: 'L’aperçu n’a pas pu être ouvert.',
  },
  qr: {
    kicker: 'Code QR',
    title: 'Appliquer les réglages ?',
    scan: 'Scanner le code QR',
    scanLang: 'Scanner les réglages ou la sélection',
    scanHinweis: 'Configuration ou sélection',
    uebernimmt: (n: number) => (n === 1 ? 'Un réglage sera appliqué :' : `${n} réglages seront appliqués :`),
    zugang: 'Accès au serveur',
    uebrige: 'Autres réglages',
    warnung: 'Un code scanné prime sur le profil de gestion des appareils. Il contient les accès au serveur en clair — une impression n’a pas sa place sur un panneau d’affichage.',
    ignoriert: (n: number) => (n === 1 ? 'Une entrée sans équivalent' : `${n} entrées sans équivalent`),
    nichts: 'Aucun réglage ne peut être repris de ce code.',
    uebernehmen: 'Appliquer',
    abbrechen: 'Annuler',
    gesetzt: 'Réglages appliqués.',
    auswahlGesetzt: 'Sélection appliquée.',
    examFehlt: 'L’examen du code n’est pas sur cet appareil. Le télécharger d’abord, puis scanner à nouveau.',
    stationFehlt: 'La station, l’examinateur ou l’acteur du code n’existe pas dans cet examen.',
    fremd: 'Ce code QR n’appartient pas à fairexa Stage.',
    kaputt: 'Le code QR est endommagé ou incomplet.',
    fassung: 'Ce code provient d’une version plus récente de l’app.',
    geraeteart: 'Ce code est destiné à un autre type d’appareil.',
    leer: '(vide)',
    gruppe: (name: string) => `Groupe : ${name}`,
    verwerfen: 'Annuler les réglages scannés',
    namen: {
      server_url: 'Adresse du serveur',
      classic_login: 'Identifiant',
      classic_password: 'Mot de passe',
      server_protocol: 'Protocole',
      settings_password: 'Mot de passe des réglages',
      language: 'Langue',
      sheet_half_points: 'Demi-points',
      sheet_show_timer: 'Horloge de station',
      sheet_show_actors: 'Acteurs',
      save_photos_in_album: 'Copie dans Photos',
      min_brightness: 'Luminosité, niveau le plus sombre',
    } as Partial<Record<string, string>>,
  },
  setup: {
    presenceLoaded: 'téléchargé',
    presenceServer: 'à télécharger',
    presenceLocal: 'absent du serveur',
    warnStale: 'Modifié sur le serveur — pour l’appliquer, supprimer puis télécharger à nouveau. Les évaluations saisies seront perdues.',
    simulativ: 'Examen de simulation',
    simulativNote: 'Rien n’est enregistré — aucune évaluation, aucun commentaire, aucun enregistrement ne quitte cet appareil.',
    vorschau: 'Aperçu',
    vorschauNote: 'Depuis Relay, pour essayer. Rien n’est envoyé. À supprimer à la main une fois terminé.',
    warnGone: 'Le serveur ne connaît plus cet examen. Les évaluations ne peuvent plus être transmises.',
    showAll: 'Afficher tous les examens',
    download: 'Télécharger',
    downloading: 'Téléchargement…',
    deleteExam: 'Supprimer de cet appareil',
    pruefungsEinstellungen: 'Réglages de cet examen',
    pruefungsEinstellungenNote: 'S’appliquent tant que cet examen est sélectionné :',
    einstellungAn: 'activé',
    einstellungAus: 'désactivé',
    deleteArm: 'Toucher à nouveau pour supprimer',
    deleteTitle: 'Supprimer l\'examen ?',
    deleteBody: (nom: string) => `« ${nom} » sera entièrement retiré de cet appareil.`,
    deleteRatings: (candidats: number, enAttente: number) =>
      `Des évaluations de ${candidats} ${candidats === 1 ? 'candidat' : 'candidats'} y sont liées` +
      (enAttente > 0
        ? ` — ${enAttente} ${enAttente === 1 ? 'entrée n\'a' : 'entrées n\'ont'} pas encore été transmise et sera définitivement perdue.`
        : ' — toutes ont déjà été transmises.'),
    deleteConfirm: 'Supprimer définitivement',
    deleteArchiveNote: 'Les enregistrements restent pour l’instant sur l’appareil et sont supprimés définitivement à l’issue du délai réglé.',
    deleteCancel: 'Annuler',
    downloadFailed: 'Échec du téléchargement',
    refreshFailed: 'Le serveur est injoignable',
    chooseTitle: 'Configurer l\'examen',
    kicker: 'Configurer l’examen',
    exam: 'Examen',
    pickTitle: 'Sélection de l’examen',
    refresh: 'Actualiser',
    loaded: 'chargé',
    notLoaded: 'non chargé',
    station: 'Station',
    stations: (n) => (n === 1 ? '1 station' : `${n} stations`),
    examiner: 'Examinateur',
    examiners: (n) => (n === 1 ? '1 examinateur' : `${n} examinateurs`),
    actor: 'Patient simulé',
    actors: (n) => (n === 1 ? '1 patient simulé' : `${n} patients simulés`),
    close: 'Fermer',
    apply: 'Appliquer',
  },

  candidates: {
    kicker: 'Changer de candidat',
    summary: (total, rated) => `${total} candidats · ${rated} évalués`,
    search: 'Nom, n° étudiant ou d’examen',
    searchLabel: 'Rechercher un candidat',
    sortBy: 'Trier',
    sort: {
      nachname: 'Nom',
      vorname: 'Prénom',
      matrikel: 'N° étudiant',
      pruefungsnr: 'N° d’examen',
    },
    filterAll: 'Tous',
    offeneAufgaben: (n: number) => (n === 1 ? '1 en attente' : `${n} en attente`),
    komplett: 'complète',
    filterOpen: 'À faire',
    filterRated: 'Évalués',
    note: 'Lors du changement, les saisies restent sur l’appareil et réapparaissent au retour.',
    empty: 'Aucun candidat ne correspond à la recherche et au filtre.',
    scan: 'Scanner le code QR',
    close: 'Fermer',
    state: {
      wartet: 'en attente',
      laeuft: 'en cours',
      bewertet: 'évalué',
      entschuldigt: 'excusé',
    },
  },

  switchCandidate: {
    kicker: 'Changer de candidat',
    withDraft: (name) => `Les saisies pour ${name} seront réaffichées.`,
    blank: 'Une grille vide s’ouvre et le chronomètre repart de zéro.',
    kept: (name) => `Les saisies pour ${name} restent sur l’appareil.`,
    cancel: 'Annuler',
    confirm: 'Changer',
  },

  videoAnnotate: {
    play: 'Lire',
    pause: 'Pause',
    save: 'Enregistrer',
    collapse: 'Replier les barres',
    expand: 'Déplier les barres',
    cancel: 'Annuler',
    textPlaceholder: 'Légende',
    band: 'Bande',
    addBand: 'Ajouter une bande',
    deleteBand: 'Supprimer la bande',
    open: 'Annoter',
    notPlayable: 'Cet enregistrement ne peut pas être ouvert ici.',
    soundOn: 'Son activé',
    soundOff: 'Son coupé',
  },
  annotate: {
    title: 'Annotations',
    hint: 'Choisir un outil et tracer sur l\'image. Double-toucher une légende pour modifier son texte.',
    toolArrow: 'Flèche',
    toolEllipse: 'Cercle',
    toolRect: 'Rectangle',
    toolFreehand: 'Main levée',
    toolText: 'Texte',
    toolNumber: 'Numéro',
    toolCover: 'Masque',
    color: 'Couleur',
    size: 'Taille',
    sizeSmall: 'petite',
    sizeMedium: 'moyenne',
    sizeLarge: 'grande',
    undo: 'Annuler',
    redo: 'Rétablir',
    deleteMarker: 'Supprimer l\'annotation',
    clearAll: 'Supprimer toutes les annotations',
    clearAllAsk: 'Supprimer toutes les annotations de cette image ?',
    textPrompt: 'Légende',
    textPlaceholder: 'Saisir le texte …',
    readError: "Le calque d'annotations existant est illisible. L'édition est bloquée pour ne rien perdre.",
    markerCount: (n: number) => (n === 1 ? '1 annotation' : `${n} annotations`),
    close: 'Terminé',
    cancel: 'Annuler',
    ok: 'Appliquer',
  },
  comment: {
    kicker: (task) => `Commentaire · Tâche ${task}`,
    none: 'Aucun commentaire',
    entries: (n) => (n === 1 ? '1 élément' : `${n} éléments`),
    done: 'Terminé',
    tabText: 'Texte et formules',
    tabHandwriting: 'Écriture',
    tabAudio: 'Audio',
    tabPhoto: 'Photo',
    tabVideo: 'Vidéo',
    snippets: 'Formules',
    snippetCount: (n) => `${n} formules`,
    freeText: 'Texte libre',
    freeTextHint: 'Saisir un texte ou toucher une formule à gauche …',
    freeTextHintOhne: 'Saisir un texte …',
    clear: 'Vider',
    handwritingTitle: 'Commentaire manuscrit',
    handwritingHint: 'Écrire directement ici.',
    recordReady: 'prêt',
    recordRunning: 'enregistrement',
    recordStart: 'Démarrer l’enregistrement',
    recordStop: 'Arrêter l’enregistrement',
    noRecording: 'Aucun enregistrement.',
    recordingAt: (time) => `Enregistrement ${time}`,
    saved: 'enregistré',
    noFile: 'pas encore de fichier',
    play: 'Lire',
    pausePlayback: 'Mettre la lecture en pause',
    deleteRecording: 'Supprimer l’enregistrement',
    takePhoto: 'Prendre une photo',
    cameraOpening: 'Ouverture de la caméra …',
    photoCount: (n) => (n === 1 ? '1 photo' : `${n} photos`),
    noPhoto: 'Aucune photo.',
    photoAt: (time) => `Photo ${time}`,
    viewPhoto: 'Voir la photo',
    deletePhoto: 'Supprimer la photo',
    cameraUnavailable: (message) => `Caméra indisponible : ${message}`,
    recordVideo: 'Enregistrer une vidéo',
    videoCount: (n) => (n === 1 ? '1 vidéo' : `${n} vidéos`),
    mergingVideo: 'Assemblage des séquences …',
    noVideo: 'Aucune vidéo.',
    videoAt: (time) => `Vidéo ${time}`,
    playVideo: 'Lire la vidéo',
    deleteVideo: 'Supprimer la vidéo',
    deleteKicker: 'Ranger',
    deleteTitle: {
      video: 'Ranger cet enregistrement vidéo ?',
      photo: 'Ranger cette photo ?',
      audio: 'Ranger cet enregistrement audio ?',
      handwriting: 'Abandonner l’écriture manuscrite ?',
    },
    deleteArchived: 'L’enregistrement part aux archives et sera supprimé définitivement après le délai réglé.',
    deleteGone: 'Les tracés seront perdus — ils vivent dans la grille, pas dans un fichier.',
    deleteConfirm: 'Ranger',
    deleteCancel: 'Annuler',
    handwritingStrokes: (n: number) => (n === 1 ? '1 tracé' : `${n} tracés`),
    micDenied: 'Sans accès au microphone, aucun enregistrement n’est possible.',
    close: 'Fermer',
  },

  lock: {
    kicker: 'Protégé',
    title: 'Réglages',
    hint: 'Les réglages de cette app sont protégés par un mot de passe. Il se trouve dans les réglages de l’iPad, rubrique « fairexa Stage ».',
    password: 'Mot de passe',
    open: 'Ouvrir',
    wrong: 'Ce mot de passe n’est pas correct.',
  },

  pruefungsPasswort: {
    kicker: 'Mot de passe de l’examen',
    laden: 'Télécharger',
    loeschen: 'Supprimer',
    hint: 'Cet examen est protégé par un mot de passe. La surveillance le connaît.',
    password: 'Mot de passe',
    weiter: 'Continuer',
    wrong: 'Ce mot de passe n’est pas correct.',
  },

  exit: {
    kicker: 'Client d’examen',
    title: 'Quitter',
    hint: 'Saisir le code pour quitter. Il se trouve dans les réglages de l’appareil, rubrique « fairexa Stage ».',
    pin: 'Code',
    quit: 'Quitter',
    cancel: 'Poursuivre l’évaluation',
    wrong: 'Ce code n’est pas correct.',
    button: 'Quitter',
    menu: 'Quitter l’examen …',
    menuFile: 'Fichier',
    menuEdit: 'Édition',
    menuView: 'Affichage',
  },

  vignette: {
    situation: 'Situation',
    task: 'Consigne',
    instructions: 'Instructions pour les examinateurs',
    material: 'Documents',
    materialMissing: 'Absent de l’appareil',
  },

  clock: {
    ahead: 'en avance',
    behind: 'en retard',
    hour: 'heure',
    hours: 'heures',
    minute: 'minute',
    minutes: 'minutes',
    seconds: 'secondes',
  },
  settings: {
    groupDevice: 'Cet appareil',
    deviceName: 'Nom',
    deviceNamePlaceholder: 'p. ex. appareil d\'examen 3',
    deviceNameNote: 'Le nom figure dans chaque signature et accompagnera chaque enregistrement de cet appareil.',
    deviceId: 'Identifiant',
    clockOffTitle: 'L\'horloge de cet appareil est fausse',
    clockOffBody: (abweichung: string) =>
      `Elle diffère de ${abweichung} par rapport à l\'heure réseau. Les horodatages de cet appareil sont faux.`,
    clockOffFix: 'Corrigez-la dans les réglages sous « Général › Date et heure ».',
    clockOffClose: 'Compris',
    title: 'Apparence et grille',
    kicker: 'Réglages',
    accent: 'Couleur d’accent',
    language: 'Langue',
    managedByMdm: 'Défini par la gestion de flotte — non modifiable ici.',
    passwortStand: (gesetzt) =>
      gesetzt ? 'Un mot de passe est défini.' : 'Aucun mot de passe – les réglages sont ouverts à tous.',
    passwortNeu: 'Nouveau mot de passe',
    passwortWiederholen: 'Répéter',
    passwortSetzen: 'Définir le mot de passe',
    passwortEntfernen: 'Supprimer le mot de passe',
    passwortUngleich: 'Les deux saisies ne correspondent pas.',
    passwortGesetzt: 'Défini. Il s’applique dès que l’app passe en arrière-plan ou que l’appareil se met en veille.',
    passwortEntfernt: 'Supprimé.',
    passwortAusQr: 'Défini par un code QR — non modifiable ici.',
    brightness: 'Luminosité',
    brightnessFixed: 'Défini par la surveillance',
    languageNote:
      'S’applique uniquement à l’interface. Le contenu de l’examen reste tel qu’il a été saisi.',
    erklaerung: 'Explication',
    ausPruefung: 'Défini par l’examen',
    inPruefungGeaendert: 'Modifié pour cet examen — valable jusqu’au changement d’examen',
    groupSheet: 'Grille',
    halfPoints: 'Autoriser les demi-points',
    halfPointsNote: 'La feuille de points propose alors des pas de 0,5.',
    showTimer: 'Afficher le temps restant en haut',
    showTimerNote: 'Toucher pour démarrer, mettre en pause et réinitialiser.',
    groupRecording: 'Enregistrement',
    videoMaxNote: 'Durée maximale d\'une vidéo, toutes séquences confondues.',
    minutes: (n: number) => (n === 1 ? '1 minute' : `${n} minutes`),
    groupArchive: 'Archive des examens supprimés',
    archiveNote: 'Durée pendant laquelle les enregistrements d’un examen supprimé restent sur l’appareil.',
    months: (n: number) => (n === 1 ? '1 mois' : `${n} mois`),
    groupAfterSubmit: 'Après l’envoi',
    scanner: 'Ouvrir ensuite le scanner QR',
    scannerNote: 'Sinon, le candidat suivant est choisi dans la liste.',
    haptics: 'Retour haptique',
    hapticsNote: 'Brève réponse à chaque appui sur une ligne de la liste.',
    groupTrial: 'Essai',
  },

  server: {
    group: 'Serveur',
    url: 'Adresse',
    urlPlaceholder: 'https://relay.example:8111',
    login: 'Identifiant',
    password: 'Mot de passe',
    save: 'Enregistrer et tester',
    testing: 'Test …',
    connected: (zeit: string) => `L’accès fonctionne. Heure du serveur ${zeit}.`,
    connectedOhneZeit: 'L’accès fonctionne. Ce serveur n’indique pas d’heure — le décalage reste non mesuré.',
    drift: (sekunden: number) => `Attention : l'horloge de cet appareil diffère de ${sekunden} s de celle du serveur.`,
    failed: (grund: string) => `Pas de connexion : ${grund}`,
    none: 'Aucun serveur configuré — l\'app travaille seule sur cet appareil.',
    sendNow: 'Envoyer maintenant',
    sent: (n: number) => `${n} envoyées`,
    downloadOhnePunkte:
      "L'examen est arrivé sans points : le serveur ne livre ni échelons ni points pour ses lignes de réponse. La grille les affiche comme une liste à cocher à un point chacune. Prévenir le responsable de l'examen avant de noter.",
    mode: 'Protocole',
    messageFromServer: (text: string) => `Message du serveur : ${text}`,
    sendStuck: (n: number) =>
      n === 1
        ? "1 entrée ne peut pas être transmise — voir le journal."
        : `${n} entrées ne peuvent pas être transmises — voir le journal.`,
    passwordVolatile: 'Le mot de passe n\'a pas pu être conservé (trousseau). Il ne vaut que pour cette session.',
    connect: 'Tester l’accès',
  },

  submit: {
    kicker: 'Envoyer l’évaluation',
    noComment: '—',
    totalLabel: 'Total',
    openTasks: (names, count) =>
      count === 1
        ? `Une tâche n’est pas encore évaluée : ${names}.`
        : `${count} tâches ne sont pas encore évaluées : ${names}.`,
    screenshotNote: 'Une capture de la grille est enregistrée et associée au candidat. ',
    cancel: 'Annuler',
    confirm: 'Envoyer',
  },

  locked: {
    kicker: 'Envoyée',
    title: 'Évaluation envoyée',
    meta: (score, max, at) => `${score} / ${max} points · ${at}`,
    metaOhnePunkte: (at) => at,
    note: 'La grille est verrouillée et reste visible pour contrôle.',
    next: (name) => `Suivant : ${name}`,
    scanNext: 'Scanner le candidat suivant',
    fromList: 'Suivant depuis la liste',
  },

  belege: {
    group: 'Pièces',
    holen: 'Enregistrer les pièces en ZIP',
    laeuft: 'Compression …',
    hinweis:
      'Enregistrements, captures de la grille et attestations de ce navigateur — y compris l’archive des examens supprimés. Sur l’iPad, les mêmes fichiers passent par le Finder.',
    leer: 'Aucune pièce disponible.',
    fertig: (dateien: number, mb: number) =>
      dateien === 1 ? `1 fichier enregistré (${mb} Mo).` : `${dateien} fichiers enregistrés (${mb} Mo).`,
    fehler: 'L’enregistrement a échoué :',
  },

  persistence: {
    resumed: 'État restauré',
    writeFailed: 'Enregistrement impossible — prévenez le responsable',
    deleteFailed: 'Impossible de supprimer le fichier — l’entrée reste supprimée',
    startupFailed:
      'Impossible de charger l’examen. Merci de redémarrer l’application ou de prévenir le responsable.',
    demoInstalled: 'Examen de démonstration installé',
    pfadZuLang:
      'Le dossier des preuves est trop profond — les enregistrements risquent de ne pas être sauvegardés. Veuillez prévenir le responsable de l’examen.',
  },

  sleep: {
    sleep: 'Assombrir l\'écran',
    wake: 'Réveiller',
    wakeHint: 'Appuyez ici pour continuer',
  },

  scanner: {
    title: 'Scanner le candidat suivant',
    window: 'Fenêtre de temps',
    hint: 'Placer le code QR du candidat dans le cadre',
    unknown: (code) => `Aucun candidat pour cette carte : ${code}`,
    einrichtungscode: 'Ceci est un code de configuration — à scanner dans la fiche de configuration, pas ici.',
    cancel: 'Annuler',
    scan: 'Scanner le code QR',
    noCamera: 'Sans caméra : candidat suivant',
    ariaLabel: 'Scanner la carte',
    timeoutTitle: 'Aucune carte détectée',
    timeoutText: (seconds) =>
      `Après ${seconds} secondes sans scan : déconnecter le candidat et libérer la station, ou revenir à la dernière grille.`,
    back: 'Revenir à la grille',
    logout: 'Déconnecter le candidat',
  },
};
